import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from "react";
import { api } from "../api/client";
import type { AppInfo, Job } from "../api/types";

interface Toast {
  id: number;
  kind: "info" | "error" | "success";
  message: string;
}

interface AppState {
  info: AppInfo | null;
  loading: boolean;
  error: string | null;
  jobs: Job[];
  activeJobs: Job[];
  toasts: Toast[];
  notify: (message: string, kind?: Toast["kind"]) => void;
  dismiss: (id: number) => void;
  refreshJobs: () => void;
  capability: (id: string) => AppInfo["capabilities"][number] | undefined;
}

const Ctx = createContext<AppState | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [info, setInfo] = useState<AppInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastId = useRef(1);

  const notify = useCallback((message: string, kind: Toast["kind"] = "info") => {
    const id = toastId.current++;
    setToasts((t) => [...t, { id, kind, message }]);
    if (kind !== "error") setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 4200);
  }, []);
  const dismiss = useCallback((id: number) => setToasts((t) => t.filter((x) => x.id !== id)), []);

  const refreshJobs = useCallback(() => {
    api.jobs().then((r) => setJobs(r.jobs)).catch(() => undefined);
  }, []);

  useEffect(() => {
    api
      .app()
      .then(setInfo)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
    refreshJobs();
  }, [refreshJobs]);

  // Job progress arrives over SSE. EventSource reconnects on its own, so a
  // backend restart mid-session recovers without a page reload.
  useEffect(() => {
    const es = new EventSource(api.jobEventsUrl());
    es.onopen = refreshJobs;
    const poll = window.setInterval(refreshJobs, 15000);
    window.addEventListener("online", refreshJobs);
    es.onmessage = (ev) => {
      try {
        const job: Job = JSON.parse(ev.data);
        setJobs((prev) => {
          const next = prev.filter((j) => j.id !== job.id);
          return [job, ...next].slice(0, 200);
        });
      } catch { /* a malformed frame is not worth tearing the stream down */ }
    };
    return () => { es.close(); clearInterval(poll); window.removeEventListener("online", refreshJobs); };
  }, [refreshJobs]);

  const activeJobs = useMemo(
    () => jobs.filter((j) => j.state === "queued" || j.state === "running"),
    [jobs],
  );

  const capability = useCallback(
    (id: string) => info?.capabilities.find((c) => c.id === id),
    [info],
  );

  const value: AppState = {
    info, loading, error, jobs, activeJobs, toasts, notify, dismiss, refreshJobs, capability,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useApp(): AppState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useApp must be used inside <AppProvider>");
  return ctx;
}
